<template>
  <div :class="$style.container">
    <div
      v-if="!isOrgMdmEnrolled"
      :class="$style.content"
    >
      <div :class="$style.intro">
        <div
          ref="isOrgMdmEnrolled"
        >
          <h2 :class="$style.introTitle">
            MDM Configuration
          </h2>
          <p>
            Set up MDM by uploading an Apple Push Certificate to your JumpCloud organization.
            Once configured you will be able to enroll systems into JumpCloud's MDM.
          </p>
          <FlatActionAnchor
            :buttonClass="['btnDefault', $style.actionBtn]"
            :href="mdmLink.href"
            target=""
            text="get started"
          />
        </div>
      </div>
    </div>
    <div :class="$style.content">
      <div :class="$style.intro">
        <h2 :class="$style.enrollmentTitle">
          MDM Enrollment Info
        </h2>
        <div :class="$style.enrollmentInfo">
          <ul :class="$style.infoList">
            <li :class="$style.infoListItem">
              <span>MDM Enrolled</span>
              <span>{{ getFormattedMdmVendor }}</span>
            </li>
            <li :class="$style.infoListItem">
              <span>Profile Identifier</span>
              <span>{{ getFormattedProfileIdentifier }}</span>
            </li>
            <li
              v-if="isSystemMdmEnrolled"
              :class="$style.infoListItem"
            >
              <span>Supervised</span>
              <span>{{ getFormattedIsSupervised }}</span>
            </li>
          </ul>
          <ul :class="$style.infoList">
            <li :class="$style.infoListItem">
              <span>User Approved</span>
              <span>{{ getFormattedUserApproved }}</span>
            </li>
            <li :class="$style.infoListItem">
              <span>Automated Device Enrolled</span>
              <span>{{ getFormattedDepEnrollment }}</span>
            </li>
            <li
              v-if="isIOS && isSystemMdmEnrolled"
              :class="$style.infoListItem"
            >
              <span>Enrollment Type</span>
              <span
                data-test-id="SystemMdm_enrollmentTypeField"
              >{{ getEnrollmentType }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div
      v-if="isSystemMdmEnrolled"
      :class="[$style.content, $style.activationLockCard]"
    >
      <div :class="$style.intro">
        <div :class="$style.flexWrapper">
          <h2 :class="$style.enrollmentTitle">
            Activation Lock
          </h2>
          <RefreshButton
            :disabled="refreshButtonDisabledState"
            :href="mainActionLink"
            :refreshButtonText="getRefreshButtonText"
            @click="handleRefresh"
          />
        </div>
        <div :class="$style.enrollmentInfo">
          <ul :class="$style.infoList">
            <FlatActionButton
              ref="clearActivationLockButton"
              :action="handleClearActivationLockClick"
              :buttonClass="['btnDefault', $style.actionBtn]"
              :disabled="!hasActivationLockBypassCodes"
              text="clear activation lock"
            />
            <li :class="$style.infoListItem">
              <span>Bypass Code</span>
              <RetrieveBypassCode />
            </li>
            <li
              v-if="!isIOS"
              :class="$style.infoListItem"
              :data-test-id="$testId('activationLockManageable')"
            >
              <span>Activation Lock Manageable
                <IconTooltip :tooltipContainerClass="$style.tooltip">
                  <p>
                    If this field displays <strong>yes</strong>,
                    the device is enrolled in supervised MDM.
                  </p>
                  <a
                    :href="KbArticleUrls.activationLock"
                    rel="noopener noreferrer"
                    target="_blank"
                  >Read the Knowledge Base.</a>
                  <p :class="$style.secondTooltipParagraph">If this field displays
                    <strong>no</strong>,
                    the device is user enrolled.</p>
                  <a
                    :href="KbArticleUrls.enrollingDevicesWithUserApproval"
                    rel="noopener noreferrer"
                    target="_blank"
                  >Read the Knowledge Base.</a>
                </IconTooltip>
              </span>
              <span>{{ getFormattedIsActivationLockManageable }}</span>
            </li>
            <li :class="$style.infoListItem">
              <span>Activation Lock Allowed While Supervised
                <IconTooltip :tooltipContainerClass="$style.tooltip">
                  <p>
                    If the field displays <strong>yes</strong>, JumpCloud is applying an
                    Activation Lock policy to allow Activation Lock for this device.
                    If this field displays <strong>no</strong>, there is no Activation
                    Lock policy in effect so Activation Lock is not allowed.
                  </p>
                  <a
                    :href="KbArticleUrls.activationLock"
                    rel="noopener noreferrer"
                    target="_blank"
                  >Read the Knowledge Base.</a>
                </IconTooltip>
              </span>
              <span>{{ getFormattedActivationLockAllowedWhileSupervised }}</span>
            </li>
          </ul>
        </div>
      </div>
      <AlertContainer>
        <Alert
          v-if="errorText"
          type="error"
        >
          <span>{{ errorText }}
            <Link
              v-if="errorStatus === 403"
              :class="$style.renewLink"
              :href="KbArticleUrls.settingUpMdm"
              target="_blank"
              text="Renew the certificate."
            />
          </span>
        </Alert>
      </AlertContainer>
    </div>

    <div
      v-if="isOrgEnrolledAndSystemNotEnrolled && !isSystemDepEnrolled"
      :class="$style.content"
    >
      <div :class="$style.intro">
        <div
          :class="$style.mdmPolicy"
        >
          <div :class="$style.mdmPolicyContent">
            <svg fill="none" xmlns="http://www.w3.org/2000/svg" :class="$style.mdmPolicyIcon" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><circle cx="22.5" cy="22.5" r="22.5" fill="#F8EED9"/><path fill-rule="evenodd" clip-rule="evenodd" d="M13.165 15.943a37.724 37.724 0 018.517-1.42v8.517H14.07a8.23 8.23 0 01-.258-.903 22.897 22.897 0 01-.646-6.194zm8.517 7.228v8.258a12.1 12.1 0 007.743-8.258h-7.743z" fill="#FFB000"/><path fill-rule="evenodd" clip-rule="evenodd" d="M21.683 10.91h.517v.005a41.47 41.47 0 0111.098 1.677s3.87 17.808-11.615 22.453C6.713 31.17 9.811 13.103 9.811 13.103a45.926 45.926 0 0111.872-2.194zm10.583 2.838A35.42 35.42 0 0022.2 12.199h-.645a47.21 47.21 0 00-10.84 1.936c-.259 3.356-1.033 16.518 10.84 19.873a14.56 14.56 0 0010.582-11.873c.562-2.763.605-5.608.129-8.387z" fill="#FFB000"/></svg>
            <div>
              <h4>JumpCloud MDM Enrollment Policy</h4>
              <p>Remotely enroll devices in JumpCloud's MDM across your organization.</p>
            </div>
          </div>
          <FlatActionAnchor
            href="/#/configurations"
            target="_self"
          >
            go to Policies
          </FlatActionAnchor>
        </div>
      </div>
    </div>
    <div
      v-if="isOrgEnrolledAndSystemIsDepEnrolledWithDifferentVendor && isSystemDepEnrolled"
      :class="$style.depDifferentVendor"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" :class="$style.mdmPolicyInfoIcon" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="#4373c7" fill-rule="evenodd" d="M6.9 3.333c-.544 0-.9-.36-.9-.917a1.1 1.1 0 011.088-1.083c.524 0 .912.367.912.924 0 .62-.506 1.076-1.1 1.076m-2.037 6c-.408 0-.691-.215-.691-.698 0-.614.385-1.657.466-1.872L5.43 4.52c.08-.232-.022-.346-.113-.346-.418 0-.964 1.11-1.168 1.11-.08 0-.15-.09-.15-.167 0-.227.504-.764.648-.93.456-.484 1.045-.854 1.7-.854.487 0 1.012.334.6 1.569l-.815 2.481c-.07.191-.193.513-.193.716 0 .09.048.179.14.179.337 0 .958-1.086 1.12-1.086.058 0 .133.078.133.191 0 .37-1.323 1.95-2.47 1.95m6.332-6.345A5.973 5.973 0 009.012.805 5.868 5.868 0 006 0a5.868 5.868 0 00-3.012.805A5.973 5.973 0 00.805 2.988 5.871 5.871 0 000 6a5.87 5.87 0 00.805 3.012 5.974 5.974 0 002.183 2.184A5.87 5.87 0 006 12a5.87 5.87 0 003.012-.804 5.974 5.974 0 002.183-2.184C11.732 8.092 12 7.089 12 6a5.869 5.869 0 00-.805-3.012"/></svg>
      <p>
        This system has Automated Device Enrollment configured with another vendor
        and cannot be enrolled with the JumpCloud policy.
        To learn how to manage this system with JumpCloud read our
        <a
          :href="KbArticleUrls.mdmCustomPolicyMigration"
          rel="noopener noreferrer"
          target="_blank"
        >knowledge base article</a>.
      </p>
    </div>
    <div
      v-if="shouldShowEventsTable"
      :class="[$style.content, $style.cmdResults, $style.intro]"
    >
      <h2 :class="$style.introTitle">
        MDM Command Results
      </h2>
      <MdmEventsTable />
    </div>
    <div
      v-if="$hasFeatureFlag($featureFlags.ironSquidOsUpdates) && isSystemMdmEnrolled && isMacOS"
      :class="$style.content"
      :data-test-id="$testId('osUpdates')"
    >
      <div :class="$style.intro">
        <h2 :class="$style.enrollmentTitle">
          OS Updates
        </h2>
        <MdmScheduleOsUpdate
          :deviceId="getMdmDeviceId"
          :version="getVersionString"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { Link } from '@jumpcloud/ui-components';
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import Alert from 'client/components/Alert/Alert.vue';
import AlertContainer from 'client/components/Alert/AlertContainer.vue';
import FlatActionAnchor from 'components/Buttons/FlatActionAnchor.vue';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import IconTooltip from 'components/Tooltip/IconTooltip';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import MdmEventsTable from 'client/MDM/MdmEventsTable.vue';
import MdmScheduleOsUpdate from 'client/MDM/MdmScheduleOsUpdate.vue';
import MdmSettingsMixin from 'client/MDM/mixins/MdmSettingsMixin';
import ModalEventBus from 'components/Modal/ModalEventBus';
import NavPanelLinkList from 'client/components/NavPanel/NavPanelLinkList';
import RefreshButton from 'client/components/Buttons/RefreshButton.vue';
import RetrieveBypassCode from 'client/systems/RetrieveBypassCode.vue';
import SystemMixin from 'client/store/mixins/SystemMixin';

const { mdmLink } = NavPanelLinkList;

const mdmSettingsModel = StoreModelMap[ItemTypeConsts.mdmSettings];
const storeModel = StoreModelMap[ItemTypeConsts.system];
const systemModel = StoreModelMap[ItemTypeConsts.device];

export default {
  name: 'SystemMdm',

  components: {
    Alert,
    AlertContainer,
    FlatActionAnchor,
    FlatActionButton,
    IconTooltip,
    Link,
    MdmEventsTable,
    MdmScheduleOsUpdate,
    RetrieveBypassCode,
    RefreshButton,
  },

  mixins: [
    MdmSettingsMixin,
  ],

  data: () => ({
    errorStatus: '',
    errorText: '',
    hasActivationLockBypassCodes: false,
    KbArticleUrls,
    mdmLink,
    mainActionLink: '',
    refreshButtonDisabledState: false,
  }),

  computed: {
    ...mapGetters(storeModel, [
      'getEnrollmentType',
    ]),

    ...mapState(systemModel, [
      'item',
    ]),

    getFormattedActivationLockAllowedWhileSupervised() {
      return this.formatMdmBooleanToText(get(this.item, 'activationLock.deviceInformation.activationLockAllowedWhileSupervised', null));
    },

    getFormattedDepEnrollment() {
      return this.formatMdmBooleanToText(this.isSystemDepEnrolled);
    },

    getFormattedIsActivationLockManageable() {
      return this.formatMdmBooleanToText(get(this.item, 'activationLock.securityInfo.isActivationLockManageable', null));
    },

    getFormattedIsSupervised() {
      return this.formatMdmBooleanToText(get(this.item, 'activationLock.deviceInformation.isSupervised', null));
    },

    getFormattedMdmVendor() {
      return this.formatMdmBooleanToText(this.isSystemMdmVendorValid);
    },

    getFormattedProfileIdentifier() {
      const profileIdentifier = this.getSystemMdmProfileIdentifier;

      if (profileIdentifier === null || profileIdentifier === undefined) return 'not available';

      return profileIdentifier;
    },

    getFormattedUserApproved() {
      return this.formatMdmBooleanToText(this.isUserApproved);
    },

    getMdmDeviceId() {
      return get(this.item, 'mdm.internal.deviceId');
    },

    getRefreshButtonText() {
      return this.refreshButtonDisabledState ? 'New data available at next device contact' : 'Refresh';
    },

    getVersionString() {
      const { version } = this.item;
      const revision = get(this.item, 'osVersionDetail.revision');
      return `${version} (${revision})`;
    },

    isIOS() {
      return SystemMixin.isIOS(this.item);
    },
    shouldShowEventsTable() {
      return this.isSystemMdmEnrolled
        && (this.isCustomerDirectoryInsightsEnabled || this.isFreeUserSystemAndOrgEnrolled);
    },
  },

  async mounted() {
    if (this.isSystemMdmEnrolled) {
      await this.fetchMdmSettings();
      await this.retrieveActivationLockInformation();
    }
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerSuccessNotification',
    ]),

    ...mapActions(mdmSettingsModel, [
      'postClearActivationLock',
      'refreshActivationLockInformation',
      'fetchMdmSettings',
    ]),

    ...mapActions(systemModel, [
      'fetchActivationLockInformation',
    ]),

    formatMdmBooleanToEnabledOrDisabled(input) {
      if (input === null) return 'not available';

      return input ? 'enabled' : 'disabled';
    },

    formatMdmBooleanToText(input) {
      if (input === null) return 'not available';

      return input ? 'yes' : 'no';
    },

    handleClearActivationLockClick() {
      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps: {
          cancelText: '',
          continueText: 'Clear Activation Lock',
          headerText: 'Clear Activation Lock',
          mdTemplate: 'Are you sure you want to clear the Activation Lock on this device? Clearing the lock removes all Activation Lock protection for this  device and lets you bypass the Activation Lock screen.',
          handleCancel: () => this.handleModalCancel(),
          handleContinue: () => this.handleClearActivationLockContinue(),
          mainActionAttention: true,
          showClose: true,
        },
        preventCloseOnOverlayClick: true,
      });
    },

    async handleClearActivationLockContinue() {
      try {
        await this.postClearActivationLock({ deviceId: get(this.item, 'mdm.internal.deviceId') });
        this.triggerSuccessNotification({
          message: 'Activation lock cleared',
          showCloseButton: false,
        });
        this.hasActivationLockBypassCodes = false;
      } catch (error) {
        // Todo: implement triggerErrorNotification once showCloseButton
        // and auto-dismiss are implemented in PRO-1119
        const defaultError = 'The service encountered a temporary error and could not complete your clear activation lock request. Please try again later.';
        const errorMessage = get(error, 'response.data.message', defaultError);
        this.errorStatus = get(error, 'response.status');
        this.errorText = errorMessage;

        this.hasActivationLockBypassCodes = false;
      }
      ModalEventBus.close();
    },
    handleModalCancel() {
      ModalEventBus.close();
    },

    async handleRefresh() {
      this.refreshButtonDisabledState = true;
      try {
        await this.refreshActivationLockInformation({ deviceId: get(this.item, 'mdm.internal.deviceId') });
      } catch {
        this.errorText = 'Activation Lock Information unable to be queued. Please try again later';
      }
    },
    async retrieveActivationLockInformation() {
      const deviceId = get(this.item, 'mdm.internal.deviceId');
      await this.fetchActivationLockInformation({ deviceId });
      this.hasActivationLockBypassCodes = this.item.activationLock.hasActivationLockBypassCodes;
    },
  },
};
</script>

<style module>
@value (
  jcBlue,
  gradientDark1,
  gradientDark2,
  gradientLight3,
  gradientLight6
) from '~css/colors.css';

.cmdResults {
  margin-top: 15px;
}

.container {
  height: 100%;
  overflow: auto;
  padding: 10px 20px 10px;
  width: 100%;
}

@value (max-width-x-large) from '~css/breakpoints.css';
@value (jcGrey700) from '~css/brand-colors.css';

.content {
  border: 0.5px solid gradientLight3;
  border-radius: 2px;
  box-shadow: 0 0 8px 0.5px rgba(0, 0, 0, 0.045);
  margin-bottom: 0.5rem;
}

.content .enrollmentTitle,
.content .introTitle {
  color: gradientDark2;
  font-size: 16px;
  font-weight: 600;
}

.content .flexWrapper {
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
  padding: 0 1.538rem 0 0;
}

.content .actionBtn {
  margin-top: 10px;
}

.container .content p {
  margin: 0 0 15px;
}

.content .getStartedBtn {
  padding: 8px 20px;
}

.content .getStartedBtn:hover {
  text-decoration: none;
}

.depDifferentVendor {
  background-color: #d0e2ed;
  border: 1px solid jcBlue;
  display: flex;
  padding: 10px 10px 0;
}

.enrollmentInfo .infoList {
  margin: 0;
  padding: 0;
}

.infoListItem {
  display: flex;
  font-size: 12px;
  justify-content: space-between;
  margin: 15px 0;
}

.infoListItem > span:first-child {
  font-weight: bold;
  margin-right: 15px;
}

.infoListItem > span:last-child {
  text-align: right;
}

.intro {
  background-color: white;
  padding: 20px 40px;
}

.mdmPolicy {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.mdmPolicyContent {
  align-items: center;
  display: flex;
}

.mdmPolicyIcon {
  height: 45px;
  margin-right: 40px;
  width: 45px;
}

.mdmPolicyInfoIcon {
  height: 15px;
  margin-right: 10px;
  width: 15px;
}

.activationLockCard {
  background-color: inherit;
}

:global(.tooltip) .secondTooltipParagraph {
  margin-top: 1rem;
}

.renewLink {
  display: inline-block;
}

.tooltip {
  margin-left: 5px;
  position: relative;
  top: 0;
}

@media (min-width: max-width-x-large) {
  .enrollmentInfo { display: flex; }
  .enrollmentInfo > * { flex: 1; }

  .enrollmentInfo .infoList:last-child {
    margin: 0;
    padding: 0 0 0 20px;
  }

  .enrollmentInfo .infoList:first-child {
    margin: 0;
    padding: 0 20px 0 0;
  }
}
</style>
